'use client';

import setPreferredColorScheme from '@/hooks/setPreferredColorScheme';
import useColorScheme from '@/hooks/useColorScheme';
import usePreferredColorScheme from '@/hooks/usePreferredColorScheme';
import {
  AppConnection,
  AppContext,
  AppContextInfo,
  AppI18NLanguageInfo,
  AuthContext,
} from '@inway/layout';
import { AuthInfo } from '@inway/types';
import { useRouter } from 'next/navigation';
import { useEffect, useState } from 'react';
import RootContext, { RootContextInfo } from './RootContext';

interface GetAuthProps {
  isLoggedIn?: boolean;
  userId?: string;
  userName?: string;
  signOut?: () => Promise<void>;
}

const getAuth = ({
  isLoggedIn,
  userId,
  userName,
  signOut,
}: GetAuthProps = {}): AuthInfo => {
  return {
    user: {
      _id: userId ?? '',
      createdAt: new Date(),
      username: userName ?? '',
      services: {
        inway: {
          organization: {
            _id: '',
            name: '',
          },
          username: '',
          settings: {
            firstName: '',
            lastName: '',
            name: '',
          },
        },
      },
    },
    userId: userId ?? '',
    loggedIn: !!isLoggedIn,
    // When no signout function is provided, show working animation
    inProgress: !signOut,
    async signOut() {
      if (!signOut) {
        console.warn('signOut not implemented');
      } else {
        await signOut();
      }
    },
    has: () => false,
    hasAll: () => false,
    hasAny: () => false,
    hasNone: () => false,
  };
};

const useConnection = (): AppConnection => {
  return {
    status: 'connected',
    connected: true,
    retryCount: 0,
    retryTime: undefined,
    reason: undefined,
    reconnect: () => {},
  };
};

interface ProvidersProps {
  cookieDomain: string;
  isLoggedIn?: boolean;
  languages: AppI18NLanguageInfo[];
  lng: string;
  logoUrl?: string;
  packageInfo: any;
  userId?: string;
  userName?: string;
}

export default function Providers({
  children,
  cookieDomain,
  isLoggedIn,
  logoUrl = '/logo.svg',
  lng,
  languages,
  packageInfo,
  userId,
  userName,
}: React.PropsWithChildren<ProvidersProps>) {
  const router = useRouter();
  const colorScheme = useColorScheme();
  const connection = useConnection();
  const [auth, setAuth] = useState(getAuth());
  const [root, setRoot] = useState<RootContextInfo>({
    loggedIn: () => {},
    loggedOut: () => {},
  });

  // console.log(packageInfo);

  const app: AppContextInfo = {
    module: 'auth',
    version: packageInfo.version,
    connection,
    theme: {
      colorScheme: useColorScheme(),
      preferredColorScheme: usePreferredColorScheme(),
      setPreferredColorScheme: (scheme) =>
        setPreferredColorScheme(scheme, cookieDomain),
      iconStyle: 'thin',
      logo: {
        src: logoUrl,
        width: 58,
        height: 58,
        className: 'logo-default',
      },
      menuStyle: 'hidden',
    },
    i18n: {
      lng,
      languages,
    },
  };

  useEffect(() => {
    const loggedOut = () =>
      setAuth(getAuth({ isLoggedIn: false, signOut: async () => {} }));

    const signOut = async () => {
      await fetch('/api/logout', {
        method: 'POST',
        mode: 'same-origin',
        credentials: 'same-origin',
        cache: 'no-cache',
        referrerPolicy: 'same-origin',
        redirect: 'error',
      });

      if (router) router.replace('/', { scroll: false });

      loggedOut();
    };

    const loggedIn = ({ userId, userName }) =>
      setAuth(
        getAuth({
          isLoggedIn: true,
          userId,
          userName,
          signOut,
        })
      );

    setRoot({ loggedIn, loggedOut });
    setAuth(getAuth({ isLoggedIn, userId, userName, signOut }));
  }, [isLoggedIn, userId, userName, router, setAuth, setRoot]);

  return (
    <RootContext.Provider value={root}>
      <AppContext.Provider value={app}>
        <AuthContext.Provider value={auth}>{children}</AuthContext.Provider>
      </AppContext.Provider>
    </RootContext.Provider>
  );
}
